/* ============================================================
 * DADDL Bundle Offers – <daddl-bundle-builder>
 * ============================================================
 * Bundle-builder modal opened from the tier offer cards
 * (snippets/daddl-bundle-offers.liquid).
 *
 * - Selection is a Map of variantId -> qty; tapping a flavor card
 *   increments, the minus button / tray thumbnail decrements.
 * - The milestone progress bar measures TOTAL bags: eligible bags
 *   already in the cart (seeded from /cart.js on every open) plus
 *   the current selection. The Shopify automatic discounts are
 *   cart-wide, so the modal must count the same way.
 * - Pricing is intentionally not computed here: the Buy X Get Y
 *   automatic discounts created in the admin are the source of
 *   truth, and the cart drawer shows the discounted lines.
 * ============================================================ */

(function () {
  'use strict';

  if (customElements.get('daddl-bundle-builder')) return;

  class DaddlBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const configEl = this.querySelector('[data-bundle-config]');
      const config = configEl ? JSON.parse(configEl.textContent) : { milestones: [], strings: {} };
      this.milestones = (config.milestones || []).sort((a, b) => a.count - b.count);

      /* Liquid's `t` filter HTML-escapes its output, and JSON script tags
       * are NOT entity-decoded by the browser – decode once here so
       * apostrophes etc. render correctly via textContent. */
      this.strings = {};
      Object.entries(config.strings || {}).forEach(([key, value]) => {
        this.strings[key] = decodeEntities(value);
      });

      this.selection = new Map(); // variantId -> qty
      this.cartCount = 0;
      this.opener = null;
      this.preselectDone = false;

      this.refs = {
        message: this.querySelector('[data-bundle-message]'),
        track: this.querySelector('[data-bundle-track]'),
        prev: this.querySelector('[data-bundle-prev]'),
        next: this.querySelector('[data-bundle-next]'),
        tray: this.querySelector('[data-bundle-tray]'),
        trayEmpty: this.querySelector('[data-bundle-tray-empty]'),
        addBtn: this.querySelector('[data-bundle-add]'),
        addLabel: this.querySelector('[data-bundle-add-label]'),
        addBadge: this.querySelector('[data-bundle-add-badge]')
      };

      this.items = new Map(); // variantId -> item element
      this.querySelectorAll('[data-bundle-item]').forEach((el) => {
        this.items.set(el.dataset.variantId, el);
      });

      this.bindEvents();

      /* Openers live outside this element (offer cards on the page). */
      document.querySelectorAll(`[data-daddl-bundle-open="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', () => this.open(btn));
      });

      /* Remove a stale portalled copy left behind when the theme editor
       * re-renders the section, then escape fixed-position context issues
       * by portalling to <body>. */
      document.querySelectorAll(`daddl-bundle-builder[id="${this.id}"]`).forEach((el) => {
        if (el !== this) el.remove();
      });
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    bindEvents() {
      this.querySelectorAll('[data-bundle-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.onKeyDown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this.items.forEach((item, variantId) => {
        /* Two plus triggers: the whole card and the "+" in the stepper pill. */
        item.querySelectorAll('[data-bundle-plus]').forEach((btn) => {
          btn.addEventListener('click', () => this.changeQty(variantId, 1));
        });
        const minus = item.querySelector('[data-bundle-minus]');
        if (minus) minus.addEventListener('click', () => this.changeQty(variantId, -1));
      });

      if (this.refs.addBtn) this.refs.addBtn.addEventListener('click', () => this.addToCart());

      /* Carousel arrows (desktop) — shared LTR-track helper. */
      const { track, prev, next } = this.refs;
      if (track && prev && next && typeof window.daddlBindCarousel === 'function') {
        window.daddlBindCarousel({
          track,
          prev,
          next,
          itemSelector: '[data-bundle-item]',
          stepItems: 2
        });
      }
    }

    /* ---------- open / close ---------- */

    async open(opener) {
      this.opener = opener || null;

      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);

      /* Warm up the confetti library so the first tier unlock celebrates
       * without waiting on the CDN. */
      if (typeof window.daddlConfettiPreload === 'function') window.daddlConfettiPreload();

      /* The first render after opening only records which tiers are already
       * reached – confetti fires only for tiers unlocked while picking. */
      this.reachedBaseline = null;

      /* Resolve state and paint content BEFORE the slide starts, so the
       * transform transition runs on a free main thread (no jank from the
       * cart fetch or the render happening mid-animation). The modal edits
       * the cart directly: steppers start at the current cart quantities and
       * the CTA applies the difference. An unapplied draft from a previous
       * open (saved on close) is restored so picking progress is never lost;
       * otherwise, on a fresh cart, the viewed product is pre-selected. */
      await this.seedCartCount();
      const draft = this.loadDraft();
      if (draft) {
        this.selection = draft;
      } else {
        this.selection = new Map(this.cartByVariant);
        if (this.selection.size === 0) {
          const preselect = this.querySelector('[data-bundle-item][data-preselect]');
          if (preselect) this.selection.set(preselect.dataset.variantId, 1);
        }
      }

      /* Reveal (still translated off-screen) and paint content now… */
      this.hidden = false;
      this.render();

      /* …then, once the off-screen frame is painted, trigger the slide. */
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));

      if (typeof trapFocus === 'function') {
        trapFocus(this, this.querySelector('.daddl-bundle-modal__close'));
      } else {
        const close = this.querySelector('.daddl-bundle-modal__close');
        if (close) close.focus();
      }
    }

    close() {
      this.saveDraft();
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);

      const onDone = () => {
        this.hidden = true;
      };
      const modal = this.querySelector('.daddl-bundle-modal');
      if (modal && matchMedia('(prefers-reduced-motion: no-preference)').matches) {
        modal.addEventListener('transitionend', onDone, { once: true });
        /* Safety net in case transitionend never fires (must clear the 0.4s
         * slide-down). */
        setTimeout(onDone, 600);
      } else {
        onDone();
      }
    }

    /* ---------- draft persistence ----------
     * Unapplied picks survive closing the modal (and navigating between
     * product pages) via sessionStorage. The draft is cleared once applied,
     * when it no longer differs from the cart, or when the cart has changed
     * since the draft was saved (a stale draft must never silently override
     * newer cart contents). */

    get draftKey() {
      return `daddl-bundle-draft:${this.id}`;
    }

    saveDraft() {
      try {
        if (this.hasChanges && !this.adding) {
          sessionStorage.setItem(
            this.draftKey,
            JSON.stringify({ selection: [...this.selection], cart: [...this.cartByVariant] })
          );
        } else {
          sessionStorage.removeItem(this.draftKey);
        }
      } catch (err) {
        /* Storage unavailable (private mode) – drafts just don't persist. */
      }
    }

    /* @returns {Map<string, number> | null} */
    loadDraft() {
      try {
        const raw = sessionStorage.getItem(this.draftKey);
        if (!raw) return null;
        const draft = JSON.parse(raw);

        /* Invalidate when the cart changed since the draft was saved. */
        const snapshot = new Map(draft.cart || []);
        if (snapshot.size !== this.cartByVariant.size) {
          this.clearDraft();
          return null;
        }
        for (const [variantId, qty] of this.cartByVariant) {
          if (snapshot.get(variantId) !== qty) {
            this.clearDraft();
            return null;
          }
        }

        const entries = (draft.selection || []).filter(
          ([variantId, qty]) => this.items.has(variantId) && qty > 0
        );
        return new Map(entries);
      } catch (err) {
        return null;
      }
    }

    clearDraft() {
      try {
        sessionStorage.removeItem(this.draftKey);
      } catch (err) {
        /* noop */
      }
    }

    /* Count eligible bags already in the cart (total + per variant) so
     * progress matches the cart-wide automatic discounts and each flavor
     * card can show its own "N in cart" chip. */
    async seedCartCount() {
      this.cartByVariant = new Map();
      this.cartCount = 0;
      try {
        const res = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart.js`, {
          credentials: 'same-origin'
        });
        const cart = await res.json();
        (cart.items || []).forEach((line) => {
          const variantId = String(line.variant_id);
          if (!this.items.has(variantId)) return;
          this.cartByVariant.set(variantId, (this.cartByVariant.get(variantId) || 0) + line.quantity);
          this.cartCount += line.quantity;
        });
      } catch (err) {
        /* Progress falls back to selection-only counting. */
      }
    }

    /* ---------- state ---------- */

    changeQty(variantId, delta) {
      const next = Math.max(0, (this.selection.get(variantId) || 0) + delta);
      if (next === 0) {
        this.selection.delete(variantId);
      } else {
        this.selection.set(variantId, next);
      }
      this.render(delta > 0 ? variantId : null);
    }

    get selectedCount() {
      let sum = 0;
      this.selection.forEach((qty) => (sum += qty));
      return sum;
    }

    /* The selection IS the desired cart state for eligible bags, so the
     * progress total is simply the selection total. */
    get totalCount() {
      return this.selectedCount;
    }

    /* True when the selection differs from the current cart contents. */
    get hasChanges() {
      if (!this.cartByVariant) return this.selection.size > 0;
      if (this.selection.size !== this.cartByVariant.size) return true;
      for (const [variantId, qty] of this.selection) {
        if (this.cartByVariant.get(variantId) !== qty) return true;
      }
      return false;
    }

    /* Highest milestone EARNED by the total bag count. A tier counts as
     * earned at its paid amount (`buy`); the gap up to `count` is the free
     * bag(s) the customer picks. */
    get reachedMilestone() {
      let reached = null;
      for (const ms of this.milestones) {
        if (this.totalCount >= (ms.buy || ms.count)) reached = ms;
      }
      return reached;
    }

    /* ---------- rendering ---------- */

    render(bumpVariantId) {
      this.renderItems(bumpVariantId);
      this.renderTray();
      this.renderProgress();
      this.renderFooter();
    }

    renderItems(bumpVariantId) {
      this.items.forEach((item, variantId) => {
        const qty = this.selection.get(variantId) || 0;
        const stepper = item.querySelector('[data-bundle-stepper]');
        const qtyEl = item.querySelector('[data-bundle-item-qty]');
        item.classList.toggle('is-selected', qty > 0);
        if (stepper) stepper.hidden = qty === 0;
        if (qtyEl) {
          qtyEl.textContent = qty;
          if (variantId === bumpVariantId) {
            qtyEl.classList.remove('is-bumping');
            void qtyEl.offsetWidth; /* restart animation */
            qtyEl.classList.add('is-bumping');
          }
        }

        /* "N in cart" chip: only shown while the draft quantity differs from
         * the cart, i.e. there is a pending change for this flavor. */
        const cartChip = item.querySelector('[data-bundle-item-cart]');
        if (cartChip) {
          const inCart = (this.cartByVariant && this.cartByVariant.get(variantId)) || 0;
          cartChip.hidden = inCart === 0 || inCart === qty;
          cartChip.textContent = (this.strings.itemInCart || '').replace('__COUNT__', inCart);
        }
      });
    }

    renderTray() {
      const { tray, trayEmpty } = this.refs;
      if (!tray) return;
      tray.querySelectorAll('.daddl-bundle-tray__thumb').forEach((el) => el.remove());
      const hasSelection = this.selection.size > 0;
      if (trayEmpty) trayEmpty.hidden = hasSelection;

      this.selection.forEach((qty, variantId) => {
        const item = this.items.get(variantId);
        if (!item) return;
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'daddl-bundle-tray__thumb';
        thumb.setAttribute('aria-label', `${item.dataset.title} × ${qty}`);
        const img = document.createElement('img');
        img.src = item.dataset.thumb;
        img.alt = '';
        img.loading = 'lazy';
        const badge = document.createElement('span');
        badge.className = 'daddl-bundle-tray__thumb-qty';
        badge.textContent = qty;
        thumb.append(img, badge);
        thumb.addEventListener('click', () => this.changeQty(variantId, -1));
        tray.appendChild(thumb);
      });
    }

    renderProgress() {
      const { message } = this.refs;
      const total = this.totalCount;

      /* Segmented bar: each tier's segment fills over the range between the
       * previous tier's paid amount and its own – the free bag(s) aren't
       * counted against the customer. */
      const reachedNow = new Set();
      let prevEarn = 0;
      this.milestones.forEach((ms) => {
        const earnAt = ms.buy || ms.count;
        const earned = total >= earnAt;
        const seg = this.querySelector(`[data-bundle-node="${ms.key}"]`);
        if (seg) {
          seg.classList.toggle('is-reached', earned);
          const segFill = seg.querySelector('[data-bundle-seg-fill]');
          if (segFill) {
            const span = Math.max(earnAt - prevEarn, 1);
            const pct = Math.min(100, Math.max(0, ((total - prevEarn) / span) * 100));
            segFill.style.width = `${pct}%`;
          }
        }
        if (earned) reachedNow.add(ms.key);
        prevEarn = earnAt;
      });
      this.celebrateNewTiers(reachedNow);

      if (message) {
        const nextMs = this.milestones.find((ms) => total < ms.count);
        const reached = this.reachedMilestone;
        let html;
        if (!nextMs) {
          html = msgIcon('trophy') + escapeHtml(this.strings.allUnlocked || '');
        } else if (total >= (nextMs.buy || nextMs.count)) {
          /* Free-pick window: the paid bags are in, the remaining gap to the
           * milestone is the free bag(s) – invite picking them, don't make it
           * sound like more bags to buy. */
          html = msgIcon('gift') + fill2(this.strings.pickFree, nextMs.count - total, nextMs.reward);
        } else if (reached) {
          html =
            msgIcon('circle-check') +
            fill3(this.strings.unlockedNext, reached.reward, (nextMs.buy || nextMs.count) - total, nextMs.reward);
        } else if (total > 0) {
          html = msgIcon('bag-shopping') + fill2(this.strings.addMore, (nextMs.buy || nextMs.count) - total, nextMs.reward);
        } else {
          html = msgIcon('hand-pointer') + escapeHtml(this.strings.pickFlavors || '');
        }
        message.innerHTML = html || '&nbsp;';
      }
    }

    /* Confetti burst for each tier that JUST became reached, reusing the
     * cart drawer's confetti (window.daddlConfettiBurst, assets/daddl-cart.js)
     * so the celebration looks identical everywhere. */
    celebrateNewTiers(reachedNow) {
      if (this.reachedBaseline !== null && typeof window.daddlConfettiBurst === 'function') {
        let index = 0;
        reachedNow.forEach((key) => {
          if (this.reachedBaseline.has(key)) return;
          const badge = this.querySelector(`[data-bundle-node="${key}"] [data-bundle-badge]`);
          window.setTimeout(() => window.daddlConfettiBurst(badge), 150 + index * 300);
          index += 1;
        });
      }
      this.reachedBaseline = reachedNow;
    }

    renderFooter() {
      const { addBtn, addLabel, addBadge } = this.refs;
      const selected = this.selectedCount;

      /* CTA states: nothing picked yet -> prompt; selection matches the
       * cart -> nothing to do; cart empty -> "Add N"; otherwise -> "Update
       * cart · N bags" (quantity changes and removals included). The bag
       * count lives IN the button – no separate counter element. */
      const isEmpty = selected === 0 && this.cartCount === 0;
      if (addBtn) addBtn.disabled = isEmpty || !this.hasChanges || this.adding === true;
      if (addLabel && !this.adding) {
        if (isEmpty) {
          addLabel.textContent = this.strings.ctaEmpty || '';
        } else if (this.cartCount === 0) {
          addLabel.textContent = (this.strings.ctaAdd || '').replace('__COUNT__', selected);
        } else {
          const bags =
            selected === 1
              ? this.strings.selectedOne || ''
              : (this.strings.selectedOther || '').replace('__COUNT__', selected);
          addLabel.textContent = (this.strings.ctaUpdate || '') + (selected > 0 ? ` · ${bags}` : '');
        }
      }
      if (addBadge) {
        const freeBags = this.reachedMilestone ? this.reachedMilestone.freeBags : 0;
        addBadge.hidden = freeBags === 0 || selected === 0;
        addBadge.textContent = (this.strings.ctaFree || '').replace('__COUNT__', freeBags);
      }
    }

    /* ---------- apply to cart ---------- */

    /* Sync the cart's eligible bags to the selection: new flavors go through
     * /cart/add.js, then a single /cart/update.js applies quantity changes
     * and removals (keyed by variant id – lines merge because we add without
     * line-item properties), tags the cart with a `daddl_bundle_builder`
     * attribute so orders built here are trackable (the order-level
     * equivalent of pick-and-mix's line-item property), and renders the
     * drawer sections for the final cart state. */
    async addToCart() {
      if (!this.hasChanges || this.adding) return;
      this.adding = true;

      const { addBtn, addLabel } = this.refs;
      addBtn.disabled = true;
      addLabel.textContent = this.strings.adding || '';

      const additions = [];
      const updates = {};
      const variantIds = new Set([...this.selection.keys(), ...this.cartByVariant.keys()]);
      variantIds.forEach((variantId) => {
        const desired = this.selection.get(variantId) || 0;
        const current = this.cartByVariant.get(variantId) || 0;
        if (desired === current) return;
        if (current === 0) {
          additions.push({ id: parseInt(variantId, 10), quantity: desired });
        } else {
          updates[variantId] = desired;
        }
      });

      try {
        const cartElem = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections =
          cartElem && typeof cartElem.getSectionsToRender === 'function'
            ? cartElem.getSectionsToRender().map((s) => s.id)
            : [];

        if (additions.length > 0) {
          await postCart(`${routesCartAddUrl()}.js`, { items: additions });
        }

        const body = {
          updates,
          attributes: { daddl_bundle_builder: 'true' }
        };
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        const parsed = await postCart(`${routesCartUpdateUrl()}.js`, body);

        addLabel.textContent = this.strings.added || '';

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'daddl-bundle-builder', cartData: parsed });
        }

        if (cartElem && typeof cartElem.renderContents === 'function' && parsed && parsed.sections) {
          if (typeof cartElem.setActiveElement === 'function') {
            cartElem.setActiveElement(this.opener || document.activeElement);
          }
          cartElem.renderContents(parsed);
        }
        if (cartElem && cartElem.classList.contains('is-empty') && this.selectedCount > 0) {
          cartElem.classList.remove('is-empty');
        }

        /* The selection is now the cart state: sync the seed and drop the
         * draft so closing doesn't resurrect pre-apply picks. */
        this.cartByVariant = new Map(this.selection);
        this.cartCount = this.selectedCount;
        this.clearDraft();

        /* Hand off to the cart drawer, where the automatic discount lines
         * confirm the reward. The next open re-seeds from the cart. */
        setTimeout(() => {
          this.adding = false;
          this.close();
        }, 700);
      } catch (err) {
        console.error('DADDL Bundle Builder – cart error:', err);
        this.adding = false;
        addLabel.textContent = this.strings.error || '';
        setTimeout(() => this.render(), 2500);
        addBtn.disabled = false;
      }
    }
  }

  /* ---------- helpers ---------- */

  function routesCartAddUrl() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  function routesCartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update';
  }

  async function postCart(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`Cart error (${res.status})`);
    return res.json();
  }

  function decodeEntities(str) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(str);
    return textarea.value;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (ch) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* Leading Font Awesome icon for the progress message (FA 6 is loaded
   * globally in theme.liquid). */
  function msgIcon(name) {
    return `<i class="fa-solid fa-${name} daddl-bundle-progress__msg-icon" aria-hidden="true"></i>`;
  }

  /* "Add __COUNT__ more for __REWARD__" */
  function fill2(template, count, reward) {
    return escapeHtml(template || '')
      .replace('__COUNT__', `<strong>${count}</strong>`)
      .replace('__REWARD__', `<strong>${escapeHtml(reward)}</strong>`);
  }

  /* "__REWARD__ unlocked! Add __COUNT__ more for __NEXT__" */
  function fill3(template, reward, count, next) {
    return escapeHtml(template || '')
      .replace('__REWARD__', `<strong>${escapeHtml(reward)}</strong>`)
      .replace('__COUNT__', `<strong>${count}</strong>`)
      .replace('__NEXT__', `<strong>${escapeHtml(next)}</strong>`);
  }

  customElements.define('daddl-bundle-builder', DaddlBundleBuilder);
})();
